import {
  addHours as dfAddHours,
  addMinutes,
  differenceInYears,
  format,
  parse,
  parseISO,
} from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

const TIMEZONE = "Asia/Bangkok"; // UTC+7

/**
 * Parse date string/object to Date. Safe for Firefox (handles empty, ISO and non-ISO).
 * Returns null if invalid so callers can fallback.
 */
function toDate(value: Date | string | null | undefined): Date | null {
  if (value == null || value === "") return null;
  if (value instanceof Date)
    return Number.isNaN(value.getTime()) ? null : value;
  const s = String(value).trim();
  if (!s) return null;
  try {
    // ISO-like (yyyy-mm-dd or full ISO) — parse with date-fns for consistent behavior in Firefox
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
      const d = parseISO(s);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

/**
 * Parse `"yyyy-MM-dd"` as noon Asia/Bangkok (stable calendar date for documents).
 */
export function parseDateOnlyInBangkok(dateStr: string): Date {
  const d = dateStr.trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) {
    return parseISO(`${getTodayYYYYMMDD()}T12:00:00+07:00`);
  }
  return parseISO(`${d}T12:00:00+07:00`);
}

/**
 * Resolve a document issue date as a calendar day in Asia/Bangkok (not server local TZ).
 */
export function resolveDocumentIssueDateInBangkok(
  value: Date | string | null | undefined,
): Date {
  if (value == null || value === "") {
    return parseDateOnlyInBangkok(getTodayYYYYMMDD());
  }
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    return parseDateOnlyInBangkok(value);
  }
  const parsed = toDate(value);
  if (!parsed) {
    return parseDateOnlyInBangkok(getTodayYYYYMMDD());
  }
  return parseDateOnlyInBangkok(
    formatInTimeZone(parsed, TIMEZONE, "yyyy-MM-dd"),
  );
}

/** Normalize date-only values for PG `timestamp` columns (drizzle `mode: "string"`). */
export function toTimestampDateString(
  value: Date | string | null | undefined,
): string | null {
  if (value == null || value === "") return null;
  if (typeof value === "string") {
    const d = value.trim().slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return null;
    return `${d} 00:00:00`;
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const d = formatInTimeZone(value, TIMEZONE, "yyyy-MM-dd");
    return `${d} 00:00:00`;
  }
  return null;
}

/**
 * Format current date/time in UTC+7 timezone (Asia/Bangkok)
 * Returns format: "yyyy-MM-dd HH:mm:ss.SSS"
 */
export function formatNow(): string {
  return formatInTimeZone(new Date(), TIMEZONE, "yyyy-MM-dd HH:mm:ss.SSS");
}

/** บวกชั่วโมงจาก timestamp รูปแบบ formatNow() */
export function addHours(
  dateTimeStr: string,
  hours: number,
): string {
  const d = parseISO(dateTimeStr.replace(" ", "T"));
  const next = dfAddHours(d, hours);
  return formatInTimeZone(next, TIMEZONE, "yyyy-MM-dd HH:mm:ss.SSS");
}

/**
 * Format date in UTC+7 timezone (Asia/Bangkok)
 * @param date - Date to format (Date object or ISO string)
 * @param formatStr - Format string (default: "yyyy-MM-dd HH:mm:ss.SSS")
 */
export function formatInLao(
  date: Date | string | null | undefined,
  formatStr = "yyyy-MM-dd HH:mm:ss.SSS",
): string {
  const d = toDate(date);
  return d ? formatInTimeZone(d, TIMEZONE, formatStr) : "–";
}

// Keep existing functions for backward compatibility
export function formatDateTime(date: Date | string | null | undefined): string {
  const d = toDate(date);
  return d ? format(d, "dd/MM/yyyy HH:mm:ss") : "–";
}

export function formatDate(date: Date | string | null | undefined): string {
  const d = toDate(date);
  return d ? format(d, "dd/MM/yyyy") : "–";
}

/** ອາຍຸເຕັມປີ (ຄິດຈາກວັນເດືອນປີເກີດຖຶງມື້ນີ້) */
export function getAgeYears(
  dateOfBirth: Date | string | null | undefined,
): number | null {
  const birth = toDate(dateOfBirth);
  if (!birth) return null;
  const age = differenceInYears(new Date(), birth);
  return age >= 0 ? age : null;
}

export function formatTime(time: string): string {
  // time is in format "HH:mm:ss" or "HH:mm"
  const [hours, minutes] = time.split(":");
  return `${hours}:${minutes}`;
}

/**
 * Normalize time string to HH:mm:ss (e.g. "09:30" -> "09:30:00").
 */
export function toTimeHHmmss(timeStr: string): string {
  if (!timeStr) return "00:00:00";
  if (timeStr.length === 8 && timeStr.includes(":")) return timeStr;
  if (timeStr.length === 5) return `${timeStr}:00`;
  return timeStr;
}

/**
 * Add 1 hour to a time string (HH:mm or HH:mm:ss). Returns HH:mm:ss.
 */
export function addOneHour(timeStr: string): string {
  if (!timeStr) return "10:00:00";
  const normalized = timeStr.length === 5 ? `${timeStr}:00` : timeStr;
  const date = new Date(`2000-01-01T${normalized}`);
  date.setHours(date.getHours() + 1);
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}:${String(date.getSeconds()).padStart(2, "0")}`;
}

const TIME_ANCHOR_DD = new Date(2000, 0, 1);

/**
 * บวกนาทีให้เวลา HH:mm คืนเป็น HH:mm (ใช้แนวเดียวกับ parseTimeToDate)
 */
export function addMinutesToTimeHhMm(
  startHhMm: string,
  minutesToAdd: number,
): string {
  if (!startHhMm?.trim()) return "00:00";
  const hhmm = startHhMm.length >= 5 ? startHhMm.slice(0, 5) : startHhMm;
  const t = parse(hhmm, "HH:mm", TIME_ANCHOR_DD);
  return format(addMinutes(t, minutesToAdd), "HH:mm");
}

/**
 * Format date to readable format like "Tuesday, Oct 24, 2023"
 */
export function formatDateReadable(
  date: Date | string | null | undefined,
): string {
  const d = toDate(date);
  return d ? format(d, "EEEE, MMM dd, yyyy") : "–";
}

/**
 * วันนี้ในรูปแบบ yyyy-MM-dd (Asia/Bangkok)
 */
export function getTodayYYYYMMDD(): string {
  return formatInTimeZone(new Date(), TIMEZONE, "yyyy-MM-dd");
}

/**
 * Parse date string yyyy-MM-dd เป็น Date.
 * Returns null ถ้า invalid.
 */
export function parseDateISO(dateStr: string): Date | null {
  return toDate(dateStr);
}

/**
 * Parse time string (HH:mm หรือ HH:mm:ss) เป็น Date ใช้ 2000-01-01 เป็นฐาน.
 * สำหรับเปรียบเทียบเวลาเท่านั้น.
 */
export function parseTimeToDate(timeStr: string): Date {
  const normalized = toTimeHHmmss(timeStr ?? "");
  const d = parseISO(`2000-01-01T${normalized}`);
  return Number.isNaN(d.getTime()) ? new Date("2000-01-01T00:00:00") : d;
}

/**
 * ຟອມຂາຍແພັກ: ບັງຄັບເປັນ Date ທີ່ໃຊ້ໄດ້; ຄ່າວ່າງ ຫຼື Invalid Date → ໃຊ້ເວລາປັດຈຸບັນ (ຄື default ຂອງຟອມ).
 */
export function normalizeSellPackageStartDateValue(value: unknown): Date {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? new Date() : value;
  }
  if (value == null || value === "") {
    return new Date();
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? new Date() : d;
  }
  if (typeof value === "string") {
    const d = toDate(value);
    return d ?? new Date();
  }
  return new Date();
}
